import React, {useEffect, useRef, useState} from 'react';
import {useAuth} from '../context/AuthContext';
import {useNavigate} from 'react-router-dom';
import UserAvatar from '../components/UserAvatar';
import Icon from '../components/icons/Icon';
import Toast from '../components/Toast';
import profileImageService from '../services/profileImageService';
import './UserSettings.css';

const UserSettings = () => {
  const {user, logout, updateUserPhoto} = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState(null);

  // Toast helpers
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };
  
  const hideToast = () => {
    setToast(null);
  };

  // Apply theme class to body on mount
  useEffect(() => {
    const theme = localStorage.getItem('theme') || 'light';
    document.documentElement.classList.toggle('dark-mode', theme === 'dark');
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);

    try {
      const result = await profileImageService.uploadProfileImage(file, user?.sub || user?.id);
      
      console.log('✅ Upload successful:', result);
      
      // Create new image URL with timestamp to bust cache
      const timestamp = Date.now();
      const newImageUrl = profileImageService.buildApiUrl(`/api/user/profile-image/${user?.sub || user?.id}?t=${timestamp}`);
      
      console.log('🖼️ New image URL:', newImageUrl);
      
      // Update user photo in AuthContext (this updates it globally)
      updateUserPhoto(newImageUrl);
      
      // Show success toast
      showToast('✅ Profile image updated successfully!', 'success');
    } catch (error) {
      console.error('❌ Failed to upload profile image:', error);
      showToast(error.message || 'Failed to upload profile image', 'error');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="user-settings-content">
        <div className="user-profile-section">
          {/* Hidden file input */}
          <input
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            onChange={handleFileChange}
            ref={fileInputRef}
            style={{display: 'none'}}
            type="file"
          />
          
          {/* Profile Header Card */}
          <div className="profile-header-card">
            <div className="profile-background">
              <div className="profile-gradient"></div>
            </div>
            <div className="profile-info">
              <div className="profile-avatar-container">
                <UserAvatar 
                  size={120} 
                  user={user} 
                />
                <button 
                  className="change-photo-btn" 
                  disabled={uploading}
                  onClick={handlePhotoClick}
                  title={uploading ? 'Uploading...' : 'Change profile photo'}
                  type="button"
                >
                  {uploading ? (
                    <Icon name="loader" size={16} />
                  ) : (
                    <Icon name="camera" size={16} />
                  )}
                </button>
              </div>
              <div className="profile-details">
                <h2 className="profile-name">{user?.name || 'User'}</h2>
                <p className="profile-email">{user?.user?.email || 'user@example.com'}</p>
                <p className="profile-userid">{user?.sub || user?.id || 'N/A'}</p>
                <div className="profile-badges">
                  <span className="badge badge-primary">Pro Member</span>
                  <span className="badge badge-success">Verified</span>
                </div>
              </div>
            </div>
          </div>

          {/* Account Information */}
          <div className="settings-card">
            <div className="card-header">
              <Icon name="user" size={24} />
              <h3>Account Information</h3>
            </div>
            <div className="card-content">
              <div className="info-row">
                <label>Full Name</label>
                <div className="info-value">
                  <span>{user?.name || 'Not set'}</span>
                  <button className="edit-btn" type="button">
                    <Icon name="edit" size={16} />
                  </button>
                </div>
              </div>
              <div className="info-row">
                <label>Email Address</label>
                <div className="info-value">
                  <span>{user?.user?.email || 'Not set'}</span>
                  <button className="edit-btn" type="button">
                    <Icon name="edit" size={16} />
                  </button>
                </div>
              </div>
              <div className="info-row">
                <label>Username</label>
                <div className="info-value">
                  <span>{user?.username || 'Not set'}</span>
                  <button className="edit-btn" type="button">
                    <Icon name="edit" size={16} />
                  </button>
                </div>
              </div>
              <div className="info-row">
                <label>Organization</label>
                <div className="info-value">
                  <span>{user?.organization || 'Mia-Platform'}</span>
                  <button className="edit-btn" type="button">
                    <Icon name="edit" size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Preferences */}
          <div className="settings-card">
            <div className="card-header">
              <Icon name="sliders" size={24} />
              <h3>Preferences</h3>
            </div>
            <div className="card-content">
              <div className="preference-row">
                <div className="preference-info">
                  <label>Language</label>
                  <span className="preference-description">Choose your preferred language</span>
                </div>
                <select className="preference-select">
                  <option>English (US)</option>
                  <option>Italian</option>
                  <option>Spanish</option>
                  <option>French</option>
                </select>
              </div>
              <div className="preference-row">
                <div className="preference-info">
                  <label>Timezone</label>
                  <span className="preference-description">Your local timezone</span>
                </div>
                <select className="preference-select">
                  <option>UTC+1 (Europe/Rome)</option>
                  <option>UTC+0 (Europe/London)</option>
                  <option>UTC-5 (America/New_York)</option>
                </select>
              </div>
              <div className="preference-row">
                <div className="preference-info">
                  <label>Email Notifications</label>
                  <span className="preference-description">Receive updates via email</span>
                </div>
                <label className="toggle-switch">
                  <input defaultChecked type="checkbox" />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <div className="preference-row">
                <div className="preference-info">
                  <label>Desktop Notifications</label>
                  <span className="preference-description">Show system notifications</span>
                </div>
                <label className="toggle-switch">
                  <input defaultChecked type="checkbox" />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>

          {/* Security */}
          <div className="settings-card">
            <div className="card-header">
              <Icon name="shield" size={24} />
              <h3>Security</h3>
            </div>
            <div className="card-content">
              <div className="security-row">
                <Icon className="security-icon" name="key" size={20} />
                <div className="security-info">
                  <label>Password</label>
                  <span className="security-description">Last changed 30 days ago</span>
                </div>
                <button className="action-btn" type="button">Change</button>
              </div>
              <div className="security-row">
                <Icon className="security-icon" name="smartphone" size={20} />
                <div className="security-info">
                  <label>Two-Factor Authentication</label>
                  <span className="security-description">Add an extra layer of security</span>
                </div>
                <button className="action-btn action-btn-primary" type="button">Enable</button>
              </div>
              <div className="security-row">
                <Icon className="security-icon" name="clock" size={20} />
                <div className="security-info">
                  <label>Active Sessions</label>
                  <span className="security-description">Manage your logged-in devices</span>
                </div>
                <button className="action-btn" type="button">View</button>
              </div>
            </div>
          </div>

          {/* Danger Zone */}
          <div className="settings-card danger-card">
            <div className="card-header">
              <Icon name="alert-triangle" size={24} />
              <h3>Danger Zone</h3>
            </div>
            <div className="card-content">
              <div className="danger-row">
                <div className="danger-info">
                  <label>Sign Out</label>
                  <span className="danger-description">Sign out from this account</span>
                </div>
                <button className="danger-btn" onClick={handleLogout} type="button">
                  Sign Out
                </button>
              </div>
              <div className="danger-row">
                <div className="danger-info">
                  <label>Delete Account</label>
                  <span className="danger-description">Permanently delete your account and all data</span>
                </div>
                <button className="danger-btn danger-btn-critical" type="button">
                  Delete Account
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Toast notification */}
        {toast && (
          <Toast
            message={toast.message}
            onClose={hideToast}
            type={toast.type}
          />
        )}
    </div>
  );
};

export default UserSettings;
